import { useEffect } from "react";
import { isHlsUrl, isAppleWebKitBrowser } from "../ReusableFunctions/hlsSource";

// How long a stream may take to load its metadata before we give up on it and
// play the direct file instead. Covers players that stall silently (no error
// event) — e.g. Chrome's built-in HLS player on some versions.
export const HLS_STALL_TIMEOUT_MS = 8000;

// Attach an HLS stream URL to a media element and seek to `startSeconds`.
//   - Safari and every iPhone/iPad browser use Apple's built-in HLS player (set
//     src directly): it's reliable, and keeps lock-screen/background playback
//     and AirPlay working.
//   - Everywhere else (Chrome, Edge, Firefox, Chromebooks) uses hls.js. Chrome's
//     own built-in HLS player can stall without reporting an error on some
//     versions, while hls.js behaves the same everywhere. It is lazy-loaded so it
//     stays out of the main bundle and only downloads when a class actually has
//     a stream URL. Native HLS is still used where hls.js can't run.
// No-op when there is no stream URL — the player then renders its direct <src>
// (the existing full-file fallback).
//
// `fallbackUrl` (the direct media file) is attached if the stream can't be played:
// hls.js reports a fatal error (e.g. the streaming server rejects this origin via
// CORS, as it does for localhost/staging), the native player errors, the stream
// hasn't loaded its metadata within HLS_STALL_TIMEOUT_MS, the browser can't play
// HLS at all, or hls.js fails to load. Without a fallbackUrl the stream URL itself
// is attached. `startSeconds` is applied to whichever source ends up playing.
export default function useHlsMedia(
  mediaRef,
  streamUrl,
  startSeconds = 0,
  fallbackUrl = null,
) {
  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !streamUrl) return undefined;

    let hls = null;
    let cancelled = false;
    let fellBack = false;
    let stallTimer = null;

    const seek = () => {
      if (startSeconds > 0) {
        try {
          el.currentTime = startSeconds;
        } catch (_) {
          /* element not ready to seek — ignore */
        }
      }
    };

    const clearStallTimer = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = null;
    };

    const attachNative = (url) => {
      el.src = url;
      el.addEventListener("loadedmetadata", seek, { once: true });
    };

    // Switch to the direct file, at most once.
    const fallBack = () => {
      if (cancelled || fellBack) return;
      fellBack = true;
      clearStallTimer();
      el.removeEventListener("error", onNativeError);
      if (hls) {
        hls.destroy();
        hls = null;
      }
      attachNative(fallbackUrl || streamUrl);
    };

    const onNativeError = () => {
      if (fallbackUrl) fallBack();
    };

    // If the stream hasn't produced metadata in time, assume it's stuck.
    const startStallTimer = () => {
      if (!fallbackUrl) return;
      el.addEventListener("loadedmetadata", clearStallTimer, { once: true });
      stallTimer = setTimeout(() => {
        stallTimer = null;
        if (el.readyState < 1) fallBack(); // 1 = HAVE_METADATA
      }, HLS_STALL_TIMEOUT_MS);
    };

    const attachNativeStream = () => {
      el.addEventListener("error", onNativeError, { once: true });
      attachNative(streamUrl);
      startStallTimer();
    };

    const canPlayNativeHls = () =>
      !!(el.canPlayType && el.canPlayType("application/vnd.apple.mpegurl"));

    // Point the element straight at the stream when it isn't an HLS playlist (rare),
    // or on Safari / iPhone, which play HLS with Apple's built-in player (no hls.js download).
    if (!isHlsUrl(streamUrl) || (isAppleWebKitBrowser() && canPlayNativeHls())) {
      attachNativeStream();
    } else {
      import("hls.js")
        .then(({ default: Hls }) => {
          if (cancelled || !mediaRef.current) return;
          if (Hls.isSupported()) {
            hls = new Hls();
            hls.loadSource(streamUrl);
            hls.attachMedia(el);
            hls.on(Hls.Events.MANIFEST_PARSED, seek);
            hls.on(Hls.Events.ERROR, (_event, data) => {
              // Non-fatal errors are recovered by hls.js itself.
              if (data && data.fatal) fallBack();
            });
            startStallTimer();
          } else if (canPlayNativeHls()) {
            attachNativeStream();
          } else {
            // No MediaSource support and no native HLS: play the direct file.
            fallBack();
          }
        })
        .catch(() => {
          // hls.js failed to load — use native HLS if available, else the file.
          if (cancelled || !mediaRef.current) return;
          if (canPlayNativeHls()) attachNativeStream();
          else fallBack();
        });
    }

    return () => {
      cancelled = true;
      clearStallTimer();
      el.removeEventListener("loadedmetadata", seek);
      el.removeEventListener("loadedmetadata", clearStallTimer);
      el.removeEventListener("error", onNativeError);
      if (hls) hls.destroy();
    };
  }, [mediaRef, streamUrl, startSeconds, fallbackUrl]);
}
